import type { APIClient } from "@heidelbergmaterials/fcl-apiclient"

import {
	AdjustCarbonBalanceRequest,
	AdjustCarbonSettingRequest,
	CarbonBalanceOverviewResponse,
	CarbonSetting,
	CarbonSettingsResponse,
	ListCarbonBalanceAdjustmentsResponse,
	ListCarbonSettingAdjustmentsResponse,
	PagedList,
	Plant,
	PlantEventResponse,
	PlantEventsQueryParams,
	PlantEventTransactionIdsResponse,
	PlantEventTypesResponse,
	PlantYearsResponse,
	RemainingBufferResponse,
} from "./types"

export const getPlants = (apiClient: APIClient): Promise<Plant[]> =>
	apiClient
		.get<PagedList<Plant>>("/plants")
		.then(response => response.data.items)

export const getCarbonSettings = (
	apiClient: APIClient,
	plantId: string,
): Promise<CarbonSettingsResponse> =>
	apiClient
		.get<CarbonSettingsResponse>(`/plants/${plantId}/carbon-settings`)
		.then(response => response.data)

export const adjustCarbonSetting = (
	apiClient: APIClient,
	plantId: string,
	request: AdjustCarbonSettingRequest,
): Promise<string> =>
	apiClient
		.post<string>(`/plants/${plantId}/carbon-settings`, request)
		.then(response => response.data)

export const listCarbonSettingAdjustments = (
	apiClient: APIClient,
	plantId: string,
	year: number,
	settings?: CarbonSetting[],
): Promise<ListCarbonSettingAdjustmentsResponse> => {
	const params = new URLSearchParams({ year: String(year) })
	settings?.forEach(setting => params.append("settings", setting))

	return apiClient
		.get<ListCarbonSettingAdjustmentsResponse>(
			`/plants/${plantId}/carbon-settings-adjustments?${params.toString()}`,
		)
		.then(response => response.data)
}

export const adjustCarbonBalance = (
	apiClient: APIClient,
	plantId: string,
	request: AdjustCarbonBalanceRequest,
): Promise<string> =>
	apiClient
		.post<string>(`/plants/${plantId}/carbon-balance`, request)
		.then(response => response.data)

export const listCarbonBalanceAdjustments = (
	apiClient: APIClient,
	plantId: string,
	year: number,
): Promise<ListCarbonBalanceAdjustmentsResponse> =>
	apiClient
		.get<ListCarbonBalanceAdjustmentsResponse>(
			`/plants/${plantId}/carbon-balance-adjustments`,
			{ params: { year } },
		)
		.then(response => response.data)

export const getRemainingBuffer = (
	apiClient: APIClient,
	plantNumber: string,
	year?: number,
): Promise<RemainingBufferResponse> =>
	apiClient
		.get<RemainingBufferResponse>(
			`/plants/${plantNumber}/remaining-buffer`,
			{ params: year === undefined ? undefined : { year } },
		)
		.then(response => response.data)

export const getPlantYears = (
	apiClient: APIClient,
	plantId: string,
): Promise<PlantYearsResponse> =>
	apiClient
		.get<PlantYearsResponse>(`/plants/${plantId}/years`)
		.then(response => response.data)

export const getCarbonBalanceOverview = (
	apiClient: APIClient,
	plantId: string,
	year: number,
): Promise<CarbonBalanceOverviewResponse> =>
	apiClient
		.get<CarbonBalanceOverviewResponse>(
			`/plants/${plantId}/carbon-balance-overview`,
			{ params: { year } },
		)
		.then(response => response.data)

export const getPlantEvents = (
	apiClient: APIClient,
	plantId: string,
	{ year, limit, skip, sortedBy, filteredBy }: PlantEventsQueryParams,
): Promise<PagedList<PlantEventResponse>> => {
	const params = new URLSearchParams({ year: String(year) })

	if (limit !== undefined) {
		params.set("limit", String(limit))
	}
	if (skip !== undefined) {
		params.set("skip", String(skip))
	}
	if (sortedBy) {
		params.set("sortedBy", sortedBy)
	}
	if (filteredBy) {
		params.set("filteredBy", filteredBy)
	}

	return apiClient
		.get<
			PagedList<PlantEventResponse>
		>(`/plants/${plantId}/events?${params.toString()}`)
		.then(response => response.data)
}

export const getPlantEventTypes = (
	apiClient: APIClient,
	plantId: string,
	year: number,
): Promise<PlantEventTypesResponse> =>
	apiClient
		.get<PlantEventTypesResponse>(`/plants/${plantId}/events/event-types`, {
			params: { year },
		})
		.then(response => response.data)

export const getPlantEventTransactionIds = (
	apiClient: APIClient,
	plantId: string,
	year: number,
): Promise<PlantEventTransactionIdsResponse> =>
	apiClient
		.get<PlantEventTransactionIdsResponse>(
			`/plants/${plantId}/events/transaction-ids`,
			{ params: { year } },
		)
		.then(response => response.data)
